import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import pinia from './stores';
import vuetify from './plugins/vuetify';
// import 'vuetify/dist/vuetify.min.css';
import axios from 'axios';
import VueAxios from 'vue-axios';
import { createAuth0 } from '@auth0/auth0-vue';
import Auth0Plugin from './auth';
import { Datetime } from 'vue-datetime';
import 'vue-datetime/dist/vue-datetime.css';
import '@fontsource/manrope';
import '@carrot-kpi/switzer-font';
// import './assets/styles/custom.scss';
import VueSignalR from '@latelier/vue-signalr';
import Donut from 'vue-css-donut-chart';
import 'vue-css-donut-chart/dist/vcdonut.css';
import { vMaska } from 'maska/vue';
import VuetifyMoney from '@/components/ui/VuetifyMoney.vue';

import { createHead, VueHeadMixin } from '@unhead/vue/client';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { createIntercom } from '@mathieustan/vue-intercom';
// import vueMoment from 'vue-moment';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import 'vue3-perfect-scrollbar/style.css';
import moment from 'moment';
import bugsnagVue from './plugins/bugsnag';
import { initGlobalFilters } from './plugins/filters';
import eventBus from './plugins/eventBus';
import { setupFontAwesome } from './plugins/setupFA';

import events from 'events';
events.EventEmitter.defaultMaxListeners = 30;

const domain = import.meta.env.VUE_APP_AUTH0_CONFIG_DOMAIN,
    clientId = import.meta.env.VUE_APP_AUTH0_CONFIG_CLIENTID;

// =============================================================================
// APP LAYOUT
// =============================================================================
import AppLayout from './components/layout/AppLayout.vue';

// App
const app = createApp(App);

// =============================================================================
// INTERCOM
// =============================================================================

if (import.meta.env.VUE_APP_INTERCOM_APPID) {
    const intercom = createIntercom({ appId: import.meta.env.VUE_APP_INTERCOM_APPID });
    app.use(intercom);
}

// =============================================================================
// AUTH 0
// =============================================================================
// Expose $router before installing Auth0 so the Auth0 Vue plugin can handle
// the post-login callback and navigate to appState.target. Install the router
// after auth setup so route guards do not run before the auth wrapper exists.
app.config.globalProperties.$router = router;
app.use(
    createAuth0({
        domain,
        clientId,
        authorizationParams: {
            redirect_uri: window.location.origin,
            audience: import.meta.env.VUE_APP_AUTH0_CONFIG_AUDIENCE,
            scope: 'openid profile email offline_access',
        },
        // setting cache location to localstorage helps during development to prevent the new auth confirm popup
        // However it breaks impersonation...
        // cacheLocation: import.meta.env.DEV ? 'localstorage' : 'memory',
        // Refresh via rotating refresh tokens instead of a hidden iframe (flaky under
        // third-party-cookie restrictions). Falls back to the iframe if the Auth0
        // application isn't configured to issue refresh tokens.
        useRefreshTokens: true,
        useRefreshTokensFallback: true,
    }),
);
app.use(Auth0Plugin);
app.use(router);

app.config.globalProperties.$moment = moment;
initGlobalFilters(app);

// Font Awesome Config
setupFontAwesome(app);

app.component('Datetime', Datetime);

const head = createHead({
    init: [
        {
            titleTemplate: '%s | MemoryShare',
        },
    ],
});
app.use(head);
app.mixin(VueHeadMixin);
app.use(VueSignalR, import.meta.env.VUE_APP_SIGNALR_HUBS);
app.use(VueAxios, axios);
// app.use(VueLayers);
app.use(bugsnagVue);

app.use(Donut);
app.component('vuetify-money', VuetifyMoney);
app.use(VueSweetalert2);
app.use(PerfectScrollbarPlugin);
app.directive('mask', vMaska);
app.component('AppLayout', AppLayout);

app.directive('click-outside', {
    mounted: function (el, binding, vnode) {
        // console.log(`click-outside bind`, {el, binding, vnode});
        el.clickOutsideEvent = function (event) {
            if (!(el == event.target || el.contains(event.target))) {
                if (typeof binding.value === 'function') {
                    binding.value(event);
                }
            }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
    },
    unmounted: function (el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
    },
});
app.directive('mousehover-delayed', {
    mounted: function (el, binding, vnode) {
        if (typeof binding.value !== 'function') {
            return;
        }
        const defaultDelay = 450;
        el._timeoutId = null;
        el.addEventListener('mouseenter', () => {
            // console.log('v-mousehover triggered mouseenter');
            el._timeoutId = setTimeout(binding.value, defaultDelay);
        });

        el.addEventListener('mouseleave', () => {
            // console.log('v-mousehover triggered mouseleave');
            clearTimeout(el._timeoutId);
        });
    },
    unmounted: function (el, binding) {
        el.removeEventListener('mouseenter', binding.value);
        el.removeEventListener('mouseleave', binding.value);
    },
});
app.directive('click-single-double', {
    // Usage: v-click-single-double="{ click: () => singleClickAction(withArgument), dblclick: doubleClickAction }"
    // Note: if needing to pass arguments to the function call then you'll need to wrap it in an anonymous function

    beforeMount(el, binding, context) {
        let clickTimeout = null;
        let handledAsDoubleClick = false;
        el.addEventListener('click', event => {
            clickTimeout = setTimeout(() => {
                if (!handledAsDoubleClick) {
                    binding.value.click();
                }
            }, 200);
        });
        el.addEventListener('dblclick', event => {
            handledAsDoubleClick = true;
            clearTimeout(clickTimeout);
            binding.value.dblclick();
            setTimeout(() => (handledAsDoubleClick = false), 100);
        });
    },
});
app.directive('fancySwap', {
    updated(el, binding) {
        const { value, oldValue } = binding;
        if (value !== oldValue) {
            // console.log('image possible change detected', { oldValue, value });
            if (value) {
                const clone = el.cloneNode(false);
                clone.classList.add('clone');
                const stylesToApply = {
                    zIndex: el.style.zIndex + 1,
                    opacity: '1',
                    transition: 'opacity 250ms ease-in-out',
                };

                Object.assign(clone.style, stylesToApply);
                // console.log('image clone', clone);
                el.before(clone);
            } else {
                const clone = el.parentElement.querySelector('.clone');
                if (clone) {
                    clone.style.opacity = 0;
                    setTimeout(() => {
                        // console.log('clone should have faded out.. now removing');
                        clone.remove();
                    }, 500);
                }
            }
        }
    },
});
// https://stackoverflow.com/questions/38241480/detect-macos-ios-windows-android-and-linux-os-with-js
const userAgent = window.navigator.userAgent,
    platform = window.navigator?.userAgentData?.platform || window.navigator.platform,
    macosPlatforms = ['macOS', 'Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
    windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
    iosPlatforms = ['iPhone', 'iPad', 'iPod'];
let os = null;

if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'MacOS';
} else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS';
} else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
} else if (/Android/.test(userAgent)) {
    os = 'Android';
} else if (/Linux/.test(platform)) {
    os = 'Linux';
}
app.use(pinia).use(vuetify).provide('os', os?.toLowerCase?.()).provide('eventBus', eventBus).mount('#app');
